import { useEffect, useState } from 'react';
import {
  api,
  setup,
  invite,
  authHeaders,
  type Branding,
} from './lib/api.ts';
import { applyBranding } from './lib/branding.ts';
import { Enrol } from './ui/Enrol.tsx';
import { Login } from './ui/Login.tsx';
import { Board } from './ui/Board.tsx';
import { Setup } from './ui/Setup.tsx';
import { PhotoUpload } from './ui/PhotoUpload.tsx';

type Session = 'checking' | 'out' | 'in';

export const App = () => {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [session, setSession] = useState<Session>('checking');

  // Branding is public and resolves before any auth decision, so the
  // login screen already carries the client's colour and name. It is
  // fetched again whenever the session changes, because parts of the
  // answer depend on WHO is asking: chatOnly, userId and isAdmin all
  // come from the session. Fetched only at mount, the copy the login
  // screen received is anonymous -- and a chat-only account signed in
  // and saw the full navigation, because that stale reply had said
  // chatOnly: false.
  useEffect(() => {
    api
      .branding()
      .then((b) => {
        setBranding(b);
        applyBranding(b);
      })
      .catch(() => undefined);
  }, [session]);

  // A 401 from the board is the signal we are logged out. There is no
  // separate /me call -- the first real request answers the question.
  useEffect(() => {
    // Must carry auth: the shell authenticates with an injected
    // bearer token, and a bare fetch drops it -- the board then
    // decides it is signed out and shows a login screen to a device
    // that is correctly enrolled.
    fetch('/badges', { credentials: 'same-origin', headers: authHeaders() })
      .then((r) => setSession(r.status === 401 ? 'out' : 'in'))
      .catch(() => setSession('out'));
  }, []);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    setSession('out');
  };

  if (session === 'checking') {
    return (
      <main style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <span className="mono">Loading</span>
      </main>
    );
  }

  // /setup/<token> is the first-login link printed by provision.sh.
  const setupToken = window.location.pathname.startsWith('/setup/')
    ? window.location.pathname.slice('/setup/'.length)
    : '';

  // /photo/<token> is scanned from a QR on a counter PC. It must render
  // before any auth check -- the person holding the phone has no
  // account and never will.
  if (window.location.pathname.startsWith('/photo/')) {
    return (
      <PhotoUpload
        token={window.location.pathname.slice('/photo/'.length)}
      />
    );
  }

  const done = () => {
    window.history.replaceState({}, '', '/');
    setSession('out');
  };

  // /setup/supervisor/<userId>/<token> is the emailed invite link.
  // Matched before the plain setup path, same prefix, longer match.
  const ip = window.location.pathname;
  const parts = ip.replace('/setup/supervisor/', '').split('/');
  const isInvite = ip.startsWith('/setup/supervisor/') && parts.length === 2;

  if (isInvite) {
    const [userId, token] = parts as [string, string];
    return (
      <Setup
        branding={branding}
        onDone={done}
        heading="Set up your supervisor account"
        begin={(pw) => invite.begin(userId, token, pw)}
        confirm={(pw, code) => invite.confirm(userId, token, pw, code)}
      />
    );
  }

  if (setupToken) {
    return (
      <Setup
        branding={branding}
        onDone={done}
        begin={(pw) => setup.begin(setupToken, pw)}
        confirm={(pw, code) => setup.confirm(setupToken, pw, code)}
      />
    );
  }

  if (session === 'out') {
    // A phone can be a node or an account. Enrolment is the less
    // common path, so it sits behind a link rather than competing
    // with sign-in.
    if (enrolling) {
      return (
        <Enrol
          name={branding?.name ?? 'Kestrelay'}
          onEnrolled={() => window.location.reload()}
          onBack={() => setEnrolling(false)}
        />
      );
    }

    return (
      <Login
        branding={branding}
        onSignedIn={() => setSession('in')}
        onEnrol={() => setEnrolling(true)}
      />
    );
  }

  return <Board branding={branding} onSignOut={signOut} />;
};

